import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiHata, ROL_ETIKET, type Rol } from '../lib/api';
import { GaleriIkon, KameraIkon, OnayIkon } from '../lib/ikon';
import { GeriDugme } from '../lib/dugme';
import { useGercekTelefon } from '../lib/cihaz';
import { BELGE_KABUL, fotoYukleCoklu, pdfMi } from '../lib/foto-yukle';
import { useOturum } from '../lib/oturum';

/**
 * ŞŞ-02 — İŞ AÇMA (devir dokumani Bolum 6.2).
 * Her is acma bir KURULUM emridir. Sokum/revize buradan acilmaz —
 * kurulu iskele detayindan acilir (tek giris noktasi, tutarlilik).
 */

interface Tanimlar {
  uniteler: { id: string; ad: string }[];
  ilgililer: { id: string; ad: string; email: string; birim: string | null }[];
  calisanlar: { id: string; ad: string; rol: Rol; sicilNo: number }[];
}

export function IsAc({ onKapat, onAcildi }: { onKapat: () => void; onAcildi: () => void }) {
  const [t, setT] = useState<Tanimlar | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);

  const [isEmriNo, setIsEmriNo] = useState('');
  /**
   * YENİ İŞ EMRİ mi, VAR OLAN üzerine mi?
   *
   * ⚠️ Bir iş emri altında birden çok iskele olabiliyor — gerçek veride tek
   *    numara altında 29 iskele var. Ama numarayı yanlışlıkla tekrar yazmak da
   *    olağan. Bu yüzden seçim AÇIK: "yeni"de kayıtlı numara reddedilir,
   *    "var olan"da kabul edilir ve kâğıt fotoğrafı devralınır.
   */
  const [mevcut, setMevcut] = useState(false);
  const [emriBilgi, setEmriBilgi] = useState<{
    isSayisi: number;
    fotoVar: boolean;
    fotoNo: number | null;
    /** Devralınacak geçerli sayfa sayısı — geçersiz işaretlenenler sayılmaz. */
    sayfaSayisi: number;
    sonIs: { tanim: string; durum: string } | null;
  } | null>(null);
  /**
   * K3 — SOFT BAŞLANGIÇ: kâğıt yoksa iş yine de açılır, "todo" olarak durur.
   *
   * ⚠️ Burada bir DOSYA tutuluyor, bir "evet/hayır" değil. Önceki hâlinde
   *    “Fotoğrafı yükledim” düğmesi hiçbir şey yüklemeden K3 bayrağını açıyordu;
   *    yani “fotosuz iş kapanamaz” geçidi tek tıkla geçilebiliyordu.
   */
  /**
   * KOMUT KİMLİĞİ — form açıldığında BİR KEZ üretilir, tekrar denemede AYNI kalır.
   *
   * ⚠️ NEDEN: sunucuda `clientCommandId` üzerinden çalışan bir idempotency
   *    katmanı var, ama istemci her denemede yeni bir UUID üretiyordu — yani
   *    tam da o katmanı işlevsiz kılıyordu. Sahada olan şu oldu: iş sunucuda
   *    201 ile AÇILDI, cevap yolda kayboldu, ekranda "Failed to fetch" çıktı,
   *    kullanıcı tekrar bastı ve AYNI İŞ İKİNCİ KEZ açıldı.
   *
   * Kimlik yalnız sunucu isteği AÇIKÇA REDDETTİĞİNDE yenilenir (bkz. gonder):
   * o durumda komut uygulanmamıştır ve tekrar denemek yeni bir komuttur.
   * Ağ hatasında sonuç BİLİNMEZ — kimlik korunur ki sunucu ilk sonucu döndürsün.
   */
  const [komutId, setKomutId] = useState(() => crypto.randomUUID());
  /**
   * İŞ EMRİ KÂĞIDININ SAYFALARI — seçim sırası sayfa sırasıdır.
   *
   * ⚠️ TEK DOSYA DEĞİL (09.10.2026, Akçansa). Bu ekran 06.09'daki çok sayfa
   *    düzeltmesinin dışında kalmıştı: yalnız ilk dosya okunuyor, PDF
   *    seçilemiyordu. Şef iş emrini çoğunlukla burada yüklüyor; iki sayfalık
   *    kâğıdın ikinci sayfası iş detayına gidip ayrıca eklenmedikçe kayboluyordu.
   */
  const [dosyalar, setDosyalar] = useState<File[]>([]);
  const [onizlemeler, setOnizlemeler] = useState<string[]>([]);
  const [sonraYukle, setSonraYukle] = useState(false);
  const [fotoUyari, setFotoUyari] = useState<string | null>(null);
  const dosyaGirdisi = useRef<HTMLInputElement>(null);
  /**
   * Galeri için AYRI input — `capture` bir tercih değil KISIT: konulduğunda
   * tarayıcı galeriye geçiş sunmaz. İş emri kâğıdının fotoğrafı çoğu zaman
   * zaten telefonda oluyor (05.08.2026). Ayrıntılı gerekçe: `FotoEkle.tsx`.
   */
  const galeriGirdisi = useRef<HTMLInputElement>(null);
  /**
   * Bütün girdiler aynı yere düşer — seçim mantığı tek yerde kalsın.
   * Yeni seçim öncekilerin SONUNA eklenir: kamera tek kare çektiği için
   * "sonraki sayfayı çek" ancak böyle çalışır.
   */
  const secildi = (e: React.ChangeEvent<HTMLInputElement>) => {
    const yeni = [...(e.target.files ?? [])];
    // Aynı dosya yeniden seçilebilsin diye değer sıfırlanır (bkz. FotoEkle).
    e.target.value = '';
    if (!yeni.length) return;
    setDosyalar((v) => [...v, ...yeni]);
    setSonraYukle(false);
  };
  const cikar = (i: number) => setDosyalar((v) => v.filter((_, j) => j !== i));
  // Önizleme adresleri dosya listesiyle birlikte üretilir ve birlikte bırakılır.
  useEffect(() => {
    const adresler = dosyalar.map((f) => (pdfMi(f) ? '' : URL.createObjectURL(f)));
    setOnizlemeler(adresler);
    return () => adresler.forEach((a) => a && URL.revokeObjectURL(a));
  }, [dosyalar]);
  /** Kâğıt fotoğrafı aynı iş emrinden devralınacak mı — yükleme sorulmaz. */
  const devralinacak = mevcut && !!emriBilgi?.fotoVar;
  const kagitOzeti =
    emriBilgi && emriBilgi.sayfaSayisi > 1
      ? `${emriBilgi.sayfaSayisi} sayfa`
      : `foto ${emriBilgi?.fotoNo ?? '—'}`;
  /*
   * ⚠️ `useGercekTelefon` — `useTelefon` temsil simülasyonunda masaüstünde de
   *    true döner ve `capture` konunca tarayıcı dosya seçiciyi hiç göstermiyor.
   *    Kamera/galeri ayrımı düzen değil CİHAZ sorusudur; gerekçe FotoEkle.tsx'te.
   */
  const telefon = useGercekTelefon();
  const [uniteId, setUniteId] = useState('');
  const [tanim, setTanim] = useState('');
  const [ilgililer, setIlgililer] = useState<string[]>([]);
  const [kesifciId, setKesifciId] = useState('');
  /*
   * SAP İŞ EMRİ ALANLARI — yalnız iş emri SAP siparişi olan müşteride (SOCAR).
   * Nuh ve Akçansa'nın ekranı değişmez: bayrak kapalıysa alanlar hiç çizilmez.
   */
  const { ben } = useOturum();
  const sap = !!ben?.santiye.sapAlanlari;
  const [sorumluIsyeri, setSorumluIsyeri] = useState('');
  const [planlamaGrubu, setPlanlamaGrubu] = useState('');
  /** K6: öncelik BİLGİDİR, sırayı değiştirmez — liste yine geliş sırasıyla. */
  const [oncelik, setOncelik] = useState('');

  /*
   * Numara yazıldıkça arka planda sorulur: bu emir kayıtlı mı, kâğıt fotoğrafı
   * devralınabilir mi. Şef daha "İşi Aç"a basmadan ne olacağını görür.
   */
  useEffect(() => {
    const no = isEmriNo.trim();
    if (!mevcut || no.length < 4) {
      setEmriBilgi(null);
      return;
    }
    const z = setTimeout(() => {
      api
        .get<NonNullable<typeof emriBilgi>>(`/is-emri/${encodeURIComponent(no)}`)
        .then(setEmriBilgi)
        .catch(() => setEmriBilgi(null));
    }, 350);
    return () => clearTimeout(z);
  }, [isEmriNo, mevcut]);

  useEffect(() => {
    api
      .get<Tanimlar>('/tanimlar')
      .then((d) => {
        setT(d);
        if (d.uniteler[0]) setUniteId(d.uniteler[0].id);
      })
      .catch((e: Error) => setHata(e.message));
  }, []);

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setHata(null);
    setGonderiliyor(true);
    try {
      /*
       * ⚠️ İKİ ADIM, TEK YÖN. Fotoğraf iş açılmadan yüklenemez: dosyanın
       *    bağlanacağı jobId henüz yoktur (K8 — her fotoğraf bir işe bağlıdır).
       *    Bu yüzden önce iş açılır, sonra fotoğraf o işe yüklenir.
       */
      const { id } = await api.post<{ id: string }>('/isler', {
        clientCommandId: komutId,
        olayZamani: new Date().toISOString(),
        isEmriNo: isEmriNo.trim() || null,
        mevcutIsEmri: mevcut,
        uniteId,
        tanim,
        ilgiliKisiIds: ilgililer,
        kesifciId,
        ...(sap
          ? {
              sorumluIsyeri: sorumluIsyeri.trim() || null,
              planlamaGrubu: planlamaGrubu.trim() || null,
              oncelik: oncelik ? Number(oncelik) : null,
            }
          : {}),
      });

      /*
       * Fotoğraf yüklenemezse İŞ AÇILMIŞ SAYILIR — geri almıyoruz. Soft
       * başlangıç tam olarak bu: kâğıt eksik diye saha bekletilmez. Ama sessiz
       * de geçmiyoruz; şef fotoğrafın gitmediğini bilmeli, çünkü iş o fotoğraf
       * yüklenene kadar kapanamayacak.
       */
      if (dosyalar.length) {
        // Sırayla gider; sayfa sırası `fotoNo`dan okunur (bkz. fotoYukleCoklu).
        const sonuc = await fotoYukleCoklu(id, 'is_emri', dosyalar);
        if (sonuc.hatalar.length) {
          const liste = sonuc.hatalar.map((h) => `${h.ad}: ${h.hata}`).join(' · ');
          setFotoUyari(
            sonuc.basarili > 0
              ? `İş açıldı. Kâğıdın ${sonuc.basarili}/${dosyalar.length} sayfası yüklendi; ` +
                  `yüklenemeyen: ${liste}. Eksik sayfayı iş detayından ekleyin.`
              : `İş açıldı, ancak iş emri kâğıdı yüklenemedi (${liste}). ` +
                  'İş detayından tekrar deneyin — bu kâğıt yüklenmeden iş kapanamaz.',
          );
          setGonderiliyor(false);
          return;
        }
      }
      onAcildi();
    } catch (err) {
      /*
       * SUNUCU REDDETTİYSE komut uygulanmadı → yeni kimlik, temiz bir deneme.
       * AĞ HATASINDA sonuç bilinmez (iş açılmış olabilir) → kimlik KORUNUR;
       * tekrar denendiğinde sunucu ikinci bir iş açmak yerine ilk sonucu döner.
       */
      if (err instanceof ApiHata) {
        setKomutId(crypto.randomUUID());
        setHata(err.message);
      } else {
        setHata(
          `${err instanceof Error ? err.message : 'İş açılamadı'} — bağlantı koptu. ` +
            'Tekrar deneyin; iş zaten açıldıysa ikinci kez açılmaz.',
        );
      }
      setGonderiliyor(false);
    }
  }

  if (hata && !t) {
    return <p className="m-6 rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>;
  }
  if (!t) return <p className="m-6 text-sm text-wait">Yükleniyor…</p>;

  const gir =
    'w-full rounded-(--radius-mod) border border-line-2 px-3 py-2.5 text-sm outline-none focus:border-navy';
  const etiket = 'mb-1 block text-sm font-medium text-ink-2';

  return (
    <div className="mx-auto max-w-2xl p-4 lg:p-6">
      <GeriDugme etiket="Pano" onClick={onKapat} />
      <h1 className="mb-1 text-xl font-semibold text-navy">İş Aç</h1>
      <p className="mb-6 text-sm text-wait">Her iş açma bir kurulum emridir.</p>

      <form onSubmit={gonder} className="space-y-5">
        {/*
          İŞ EMRİ KAYNAĞI — en üstte, çünkü altındaki her şeyi etkiliyor:
          kâğıt fotoğrafı istenip istenmeyeceğini ve mükerrer kontrolünü.
        */}
        <div>
          <span className={etiket}>İş emri</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                [false, 'Yeni iş emri', 'ilk kez açılıyor'],
                [true, 'Var olan iş emri üzerine aç', 'aynı emre yeni iskele'],
              ] as const
            ).map(([d, e, n]) => (
              <button
                key={String(d)}
                type="button"
                onClick={() => setMevcut(d)}
                className={`rounded-(--radius-mod) border p-3 text-left text-sm ${
                  mevcut === d ? 'border-navy bg-navy-bg text-navy' : 'border-line-2 text-ink-2'
                }`}
              >
                <span className="block font-medium">{e}</span>
                <span className="block text-xs text-wait">{n}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={etiket} htmlFor="isEmriNo">
            İş Emri No{' '}
            <span className="font-normal text-wait">
              {mevcut ? '(var olan numarayı yazın)' : '(kâğıt yoksa boş bırakın)'}
            </span>
          </label>
          <input
            id="isEmriNo"
            className={`${gir} num`}
            inputMode="numeric"
            placeholder="4030008660"
            value={isEmriNo}
            onChange={(e) => setIsEmriNo(e.target.value)}
            required={mevcut}
          />

          {/* Arama sonucu — şef "İşi Aç"a basmadan ne olacağını görür. */}
          {mevcut && emriBilgi && (
            <div
              className={`mt-2 rounded-(--radius-mod) border p-3 text-xs leading-relaxed ${
                emriBilgi.isSayisi > 0
                  ? 'border-ok-line bg-ok-bg text-ink-2'
                  : 'border-warn-line bg-warn-bg text-warn'
              }`}
            >
              {emriBilgi.isSayisi === 0 ? (
                <>Bu numarada kayıtlı iş <strong>bulunamadı</strong>. Yeni iş emri olarak açılacak.</>
              ) : (
                <>
                  Bu iş emrinde <strong className="num">{emriBilgi.isSayisi}</strong> iskele kayıtlı
                  {emriBilgi.sonIs && <> — son: {emriBilgi.sonIs.tanim}</>}.
                  <br />
                  {emriBilgi.fotoVar ? (
                    <span className="text-ok">
                      ✓ Kâğıt fotoğrafı var ({kagitOzeti}) — <strong>otomatik
                      devralınacak</strong>, tekrar çekmenize gerek yok.
                    </span>
                  ) : (
                    <span className="text-warn">
                      ⚠ Bu emirde kâğıt fotoğrafı <strong>yok</strong> — aşağıdan yükleyin;
                      bundan sonraki iskelelere otomatik devrolur.
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>


        {/*
          K3 — SOFT BAŞLANGIÇ. İki yol YAN YANA: acil işte ikinci yol açık.
          ⚠️ Fotoğraf DEVRALINACAKSA bu blok hiç görünmüyor: aynı kâğıdı ikinci
             kez çektirmek, sahada anlamsız bir adım olurdu.
        */}
        {devralinacak ? (
          <div className="rounded-(--radius-mod) border border-ok-line bg-ok-bg p-3 text-sm">
            <p className="flex items-center gap-1.5 font-medium text-ok">
              <OnayIkon boyut={16} /> İş emri kâğıdı devralınacak
            </p>
            <p className="mt-0.5 text-xs text-wait">
              Bu iş emrinin kâğıdı sistemde var ({kagitOzeti}); yeni iskeleye kopyalanacak.
              Geçersiz işaretlenmiş sayfa kopyalanmaz. K3 kapanış geçidi açık olacak.
            </p>
          </div>
        ) : (
        <div>
          <span className={etiket}>
            İş emri kâğıdı{' '}
            <span className="font-normal text-wait">(birden çok sayfa ya da PDF olabilir)</span>
          </span>
          <input
            ref={dosyaGirdisi}
            type="file"
            /* Telefonda kamera: tek kare, görsel. Masaüstünde dosya seçici:
               çoklu ve PDF. capture YALNIZ telefonda — masaüstünde web
               kamerasını açıp şefin bilgisayarındaki dosyayı seçtirmiyor. */
            accept={telefon ? 'image/*' : BELGE_KABUL}
            {...(telefon ? { capture: 'environment' as const } : { multiple: true })}
            className="hidden"
            onChange={secildi}
          />
          {/* Galeri — yalnız telefonda; masaüstünde üstteki seçici zaten dosyaya gider. */}
          {telefon && (
            <input
              ref={galeriGirdisi}
              type="file"
              accept={BELGE_KABUL}
              multiple
              className="hidden"
              onChange={secildi}
            />
          )}

          {dosyalar.length > 0 && (
            <div className="mb-2 rounded-(--radius-mod) border border-ok-line bg-ok-bg p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-ok">
                <OnayIkon boyut={16} />
                {dosyalar.length > 1 ? `${dosyalar.length} sayfa seçildi` : 'Kâğıt seçildi'}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {dosyalar.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative">
                    {onizlemeler[i] ? (
                      <img
                        src={onizlemeler[i]}
                        alt={`Sayfa ${i + 1}`}
                        className="h-20 w-20 rounded-(--radius-mod) border border-ok-line object-cover"
                      />
                    ) : (
                      <span className="flex h-20 w-20 flex-col items-center justify-center rounded-(--radius-mod) border border-ok-line bg-surface text-xs text-wait">
                        <span className="font-semibold">PDF</span>
                        <span className="w-full truncate px-1 text-center text-[10px]">{f.name}</span>
                      </span>
                    )}
                    <span className="num absolute bottom-0 left-0 rounded-tr bg-black/55 px-1 text-[10px] text-white">
                      {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => cikar(i)}
                      aria-label={`Sayfa ${i + 1}'i kaldır`}
                      className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-line-2 bg-surface text-xs text-muted hover:text-crit"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-wait">
                İş açılınca bu sırayla yüklenecek. Kâğıt devam ediyorsa sıradaki sayfayı ekleyin.
              </p>
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => dosyaGirdisi.current?.click()}
              className="flex items-center justify-center gap-2 rounded-(--radius-mod) border border-navy-line bg-navy-bg p-3 text-sm font-medium text-navy hover:bg-navy hover:text-white"
            >
              <KameraIkon boyut={16} />
              {telefon
                ? dosyalar.length
                  ? 'Sonraki sayfayı çek'
                  : 'Kâğıdın fotoğrafını çek'
                : dosyalar.length
                  ? 'Sayfa ekle'
                  : 'Dosya seç (çoklu / PDF)'}
            </button>
            {telefon && (
              <button
                type="button"
                onClick={() => galeriGirdisi.current?.click()}
                className="flex items-center justify-center gap-2 rounded-(--radius-mod) border border-line-2 p-3 text-sm font-medium text-ink-2 active:bg-ground"
              >
                <GaleriIkon boyut={16} />
                Galeriden seç (çoklu / PDF)
              </button>
            )}
            {dosyalar.length === 0 && (
              <button
                type="button"
                onClick={() => setSonraYukle(true)}
                className={`rounded-(--radius-mod) border p-3 text-sm ${
                  sonraYukle ? 'border-warn bg-warn/10 text-warn' : 'border-line-2 text-ink-2'
                }`}
              >
                Sonra yükle (todo)
              </button>
            )}
          </div>

          {sonraYukle && !dosyalar.length && (
            <p className="mt-2 text-xs text-warn">
              İş açılır ama <strong>kâğıt fotoğrafı yüklenmeden kapanamaz</strong> (K3).
              İşlerim listenizde todo olarak kalır.
            </p>
          )}
        </div>
        )}

        <div>
          <label className={etiket} htmlFor="unite">
            Ünite
          </label>
          <select
            id="unite"
            className={gir}
            value={uniteId}
            onChange={(e) => setUniteId(e.target.value)}
          >
            {t.uniteler.map((u) => (
              <option key={u.id} value={u.id}>
                {u.ad}
              </option>
            ))}
          </select>
        </div>

        {sap && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={etiket} htmlFor="sorumluIsyeri">
                Sorumlu işyeri
              </label>
              <input
                id="sorumluIsyeri"
                className={gir}
                placeholder="STATIK"
                maxLength={40}
                value={sorumluIsyeri}
                onChange={(e) => setSorumluIsyeri(e.target.value)}
              />
            </div>
            <div>
              <label className={etiket} htmlFor="planlamaGrubu">
                Planlama grubu
              </label>
              <input
                id="planlamaGrubu"
                className={gir}
                placeholder="MET"
                maxLength={40}
                value={planlamaGrubu}
                onChange={(e) => setPlanlamaGrubu(e.target.value)}
              />
            </div>
            <div>
              <label className={etiket} htmlFor="oncelik">
                Öncelik
              </label>
              <select
                id="oncelik"
                className={gir}
                value={oncelik}
                onChange={(e) => setOncelik(e.target.value)}
              >
                <option value="">—</option>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <p className="col-span-3 -mt-1 text-xs text-wait">
              SAP sipariş formundan, isteğe bağlı. Öncelik sırayı değiştirmez; listede süzmek
              için kullanılır.
            </p>
          </div>
        )}

        <div>
          <label className={etiket} htmlFor="tanim">
            İşin Tanımı
          </label>
          <input
            id="tanim"
            className={gir}
            placeholder="Değirmen 1 elevatör bakımı için iskele"
            value={tanim}
            onChange={(e) => setTanim(e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-wait">
            Bu metin müşteriye giden bildirim mailine de girer.
          </p>
        </div>

        <div>
          <span className={etiket}>İlgili kişiler (çoklu)</span>
          {t.ilgililer.length === 0 ? (
            <p className="rounded-(--radius-mod) bg-warn/10 p-3 text-sm text-warn">
              Henüz ilgili kişi tanımlı değil. İş kapanınca bildirim maili bu kişilere gider —
              Tanımlar ekranından eklenmeli (K9: yalnız şef).
            </p>
          ) : (
            <div className="space-y-1.5 rounded-(--radius-mod) border border-line p-3">
              {t.ilgililer.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={ilgililer.includes(c.id)}
                    onChange={(e) =>
                      setIlgililer((v) =>
                        e.target.checked ? [...v, c.id] : v.filter((x) => x !== c.id),
                      )
                    }
                  />
                  <span>{c.ad}</span>
                  {c.birim && <span className="text-xs text-wait">· {c.birim}</span>}
                </label>
              ))}
            </div>
          )}
          <p className="mt-1 text-xs text-wait">
            İş kapanınca otomatik bildirim maili bu kişilere gider.
          </p>
        </div>

        <div>
          <label className={etiket} htmlFor="kesifci">
            Keşfe kim gidecek?
          </label>
          <select
            id="kesifci"
            className={gir}
            value={kesifciId}
            onChange={(e) => setKesifciId(e.target.value)}
            required
          >
            <option value="">— seçin —</option>
            {t.calisanlar.map((c) => (
              <option key={c.id} value={c.id}>
                {c.ad} · {ROL_ETIKET[c.rol]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-wait">
            Delegasyon burada yapılır; ayrı bir delegasyon adımı yoktur. Seçilen kişinin
            telefonuna düşer.
          </p>
        </div>

        {/*
          K6 — sıra geliş sırasıdır. SAP alanlı müşteride (SOCAR) öncelik alanı
          VAR ama sıraya girmez; o not yukarıda alanın altında. Bu kutu orada
          "öncelik alanı yoktur" diyerek hemen üstündeki alanla çelişiyordu.
        */}
        {!sap && (
          <p className="rounded-(--radius-mod) bg-surface-2 p-3 text-xs text-wait">
            Öncelik alanı yoktur — işler <strong>FIFO</strong> sırayla işlenir (K6).
          </p>
        )}

        {hata && (
          <p className="rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
        )}

        {/* İş AÇILDI ama foto gitmedi — geri almıyoruz, ama gizlemiyoruz da. */}
        {fotoUyari && (
          <div className="rounded-(--radius-mod) border border-warn-line bg-warn-bg p-3 text-sm text-warn">
            <p>{fotoUyari}</p>
            <button
              type="button"
              onClick={onAcildi}
              className="mt-2 rounded-(--radius-mod) bg-warn px-3 py-1.5 text-xs font-medium text-white"
            >
              Anladım, panoya dön
            </button>
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={
              gonderiliyor ||
              // Fotoğraf ya seçilmiş olmalı, ya "sonra yükle" bilinçli olarak
              // işaretlenmiş, ya da aynı emirden devralınacak (K3).
              (!dosyalar.length && !sonraYukle && !devralinacak) ||
              !tanim ||
              !kesifciId ||
              ilgililer.length === 0
            }
            className="rounded-(--radius-mod) bg-navy px-5 py-2.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {gonderiliyor
              ? dosyalar.length
                ? 'Açılıyor, kâğıt yükleniyor…'
                : 'Açılıyor…'
              : 'İşi Aç'}
          </button>
          <button type="button" onClick={onKapat} className="px-4 py-2.5 text-sm text-wait">
            Vazgeç
          </button>
        </div>
      </form>
    </div>
  );
}
